import React from 'react';

interface Props {
  children: React.ReactNode;
  /** Changing this value resets the boundary (e.g. the current route path). */
  resetKey?: string;
}

interface State {
  hasError: boolean;
}

/**
 * Catches render/lazy-load errors so a single broken route (or a stale code-split
 * chunk after a new deploy) doesn't unmount the whole app into a black screen.
 *
 * Chunk-load failures (the typical "black screen, works again after reload" case)
 * are recovered automatically by forcing a single full reload, which fetches the
 * fresh index.html with the new chunk hashes. A reload loop is prevented via a
 * short-lived sessionStorage marker.
 */
export class RouteErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: Props) {
    // Reset the error state when navigating to a different route.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error: unknown) {
    try { console.error('[RouteErrorBoundary] caught error:', error); } catch (e) { /* ignore */ }

    if (RouteErrorBoundary.isChunkLoadError(error)) {
      try {
        const KEY = 'rb_chunk_reload_at';
        const last = Number(sessionStorage.getItem(KEY) || '0');
        const now = Date.now();
        // Only auto-reload if we haven't just done so (avoid reload loops).
        if (!last || now - last > 10000) {
          sessionStorage.setItem(KEY, String(now));
          window.location.reload();
        }
      } catch (e) { /* ignore */ }
    }
  }

  static isChunkLoadError(error: unknown): boolean {
    const msg = (error && (error as any).message) ? String((error as any).message) : String(error || '');
    const name = (error && (error as any).name) ? String((error as any).name) : '';
    return (
      name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /Loading CSS chunk/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /error loading dynamically imported module/i.test(msg) ||
      /importing a module script failed/i.test(msg)
    );
  }

  private handleReload = () => {
    try { window.location.reload(); } catch (e) { /* ignore */ }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-black text-white px-6">
          <div className="text-center max-w-md">
            <h2 className="text-2xl font-bold mb-3">Etwas ist schiefgelaufen</h2>
            <p className="text-gray-400 mb-6">Die Seite konnte nicht geladen werden. Bitte lade die Seite neu.</p>
            <button
              onClick={this.handleReload}
              className="px-8 py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold hover:opacity-90 transition-opacity"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
